import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. I'll reply within 48 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Couldn't send — try again in a moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 md:py-40 border-t border-[var(--ink)] bg-[#0851F2] text-white overflow-hidden"
      data-testid="contact-section"
    >
      {/* Big EMILLY echo behind */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 -top-4 md:-top-10 text-center select-none"
        style={{
          fontFamily: "Archivo, system-ui",
          fontWeight: 900,
          fontSize: "clamp(140px, 24vw, 360px)",
          letterSpacing: "-0.06em",
          lineHeight: 0.85,
          color: "rgba(255,255,255,0.07)",
        }}
      >
        EMILLY
      </div>

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          {/* Left — info */}
          <div className="md:col-span-5">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/65">
              §06 — Contact
            </span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mt-6 font-sans-display text-5xl md:text-7xl leading-[0.95] tracking-[-0.03em] font-light uppercase"
            >
              Let's make
              <br />
              <span className="font-display italic normal-case font-light">
                something rare.
              </span>
            </motion.h2>

            <p className="mt-10 text-lg md:text-xl text-white/85 leading-relaxed max-w-[44ch]">
              Send a sketch, a half-formed idea, or a fully-baked brief — I read
              everything. Late-night Figma jam, a clean kickoff doc, or just a
              friendly hello — all welcome.
            </p>

            <div className="mt-12 space-y-8">
              <div>
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/65">
                  Email
                </span>
                <a
                  href="mailto:emilly.nekovic04@gmail.com"
                  data-testid="contact-email-link"
                  className="block mt-2 font-sans-display uppercase text-3xl md:text-4xl link-underline break-words"
                >
                  emilly.nekovic04@gmail.com
                </a>
              </div>

              <div>
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/65">
                  Based in
                </span>
                <div className="mt-2 flex items-baseline gap-3">
                  <span className="font-sans-display uppercase text-3xl md:text-4xl">
                    Belgrade, RS
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/65">
                    open to remote · worldwide
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form on a white card */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:col-span-7 md:col-start-6 bg-white text-[var(--ink)] p-8 md:p-12 border-[3px] border-[var(--ink)] shadow-[10px_12px_0_var(--ink)]"
            data-testid="contact-form"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
              ↓ Drop a line
            </div>
            <div className="mt-6 space-y-2">
              <input
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={update("name")}
                className="brutalist-input"
                data-testid="contact-input-name"
                required
              />
            </div>
            <div className="mt-6 space-y-2">
              <input
                type="email"
                placeholder="Your email"
                value={form.email}
                onChange={update("email")}
                className="brutalist-input"
                data-testid="contact-input-email"
                required
              />
            </div>
            <div className="mt-6 space-y-2">
              <input
                type="text"
                placeholder="What's it about? (optional)"
                value={form.subject}
                onChange={update("subject")}
                className="brutalist-input"
                data-testid="contact-input-subject"
              />
            </div>
            <div className="mt-6 space-y-2">
              <textarea
                placeholder="Tell me about the project — scope, timeline, dreams."
                value={form.message}
                onChange={update("message")}
                rows={5}
                className="brutalist-input resize-none"
                data-testid="contact-input-message"
                required
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="mt-10 w-full md:w-auto group flex items-center justify-between gap-12 px-10 py-6 bg-[#0851F2] hover:bg-[var(--ink)] text-white transition-colors duration-500 disabled:opacity-60 border-[3px] border-[var(--ink)] shadow-[6px_6px_0_var(--ink)] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[4px_4px_0_var(--ink)]"
            >
              <span className="font-mono text-xs uppercase tracking-[0.3em]">
                {submitting ? "Sending…" : "Send Message"}
              </span>
              {submitting ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <ArrowUpRight className="w-6 h-6 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              )}
            </button>

            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ink-soft)]">
              Avg. reply — under 48 hrs · no newsletter, ever
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
